/**
 * Main game HUD container.
 * Shows battery, speed, player count, connection status, and camera toggle.
 */

import { useGameStore, type ConnectionStatus } from '../../stores/gameStore';
import { BatteryBar } from './BatteryBar';
import { Speedometer } from './Speedometer';
import styles from './GameHUD.module.css';

function connectionLabel(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'connecting':
      return 'Connecting...';
    case 'reconnecting':
      return 'Reconnecting...';
    case 'disconnected':
      return 'Disconnected';
  }
}

function connectionColor(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return '#00ff88';
    case 'connecting':
    case 'reconnecting':
      return '#ffaa00';
    case 'disconnected':
      return '#ff4444';
  }
}

interface GameHUDProps {
  readonly fps: number;
}

export function GameHUD({ fps }: GameHUDProps) {
  const connectionStatus = useGameStore((s) => s.connectionStatus);
  const localShip = useGameStore((s) => s.localShip);
  const remotePlayers = useGameStore((s) => s.remotePlayers);
  const cameraFollow = useGameStore((s) => s.cameraFollow);
  const toggleCameraFollow = useGameStore((s) => s.toggleCameraFollow);
  const frameTimeMs = useGameStore((s) => s.liveFrameMetrics.frameTimeMs);

  const playerCount = remotePlayers.size + (localShip ? 1 : 0);

  return (
    <div className={styles.hudOverlay}>
      {/* Top right: connection + player count */}
      <div className={styles.topRight}>
        <div className={styles.connectionStatus}>
          <span
            className={styles.statusDot}
            style={{ backgroundColor: connectionColor(connectionStatus) }}
          />
          <span className={styles.statusText}>
            {connectionLabel(connectionStatus)}
          </span>
        </div>
        <div className={styles.playerCount}>
          Players: {playerCount}
        </div>
        <div className={styles.fpsCounter}>
          <span className={fps < 50 ? styles.fpsLow : styles.fpsValue}>
            {fps} FPS {frameTimeMs.toFixed(1)}ms
          </span>
        </div>
      </div>

      {/* Top left: debug buttons (always visible) */}
      <div className={styles.topLeft}>
        <button
          type="button"
          className={cameraFollow ? styles.exportButton : styles.toggleButtonActive}
          onClick={toggleCameraFollow}
        >
          Cam: {cameraFollow ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Bottom left: ship stats */}
      {localShip && (
        <div className={styles.bottomLeft}>
          <BatteryBar battery={localShip.battery} />
          <Speedometer vx={localShip.vx} vy={localShip.vy} />
        </div>
      )}

      {/* Bottom center: controls hint */}
      <div className={styles.bottomCenter}>
        <span className={styles.controlsHint}>
          WASD: Thrust/Strafe | Mouse: Aim | Ctrl/Shift: Boost
        </span>
      </div>
    </div>
  );
}
